import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Product from '../components/Product';
import Message from '../components/Message';
import Loader from '../components/Loader';
import ProductCarousel from '../components/ProductCarousel';
import Meta from '../components/Meta';
import Paginate from '../components/Paginate';
import { listProducts } from '../slices/productSlice';

// Fisher-Yates shuffle helper (outside component scope would also work)
const shuffle = (input) => {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const HomeScreen = ({ match }) => {
  const keyword = match.params.keyword;
  const pageNumber = match.params.pageNumber || 1;
  const isSearching = Boolean(keyword && String(keyword).trim().length > 0);

  const dispatch = useDispatch();

  // Use productSlice reducer
  const { products, page, pages, loading, error } = useSelector((state) => state.productList);

  // Local state to handle randomized infinite feed
  const [masterList, setMasterList] = useState([]); // full set for current keyword
  const [shuffledIds, setShuffledIds] = useState([]); // order of ids in one cycle
  const [renderIndex, setRenderIndex] = useState(0); // how many items we have revealed from shuffled order
  const [, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [loadingMoreUi, setLoadingMoreUi] = useState(false); // shows a spinner while we are about to reveal/append more
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const gridEndSentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const cycleRef = useRef(0); // counts how many shuffled cycles have been appended
  const initialOrderSetRef = useRef(false); // ensures initial shuffle runs once per feed key
  const renderIndexRef = useRef(0);
  const loadMoreStartedAtRef = useRef(0);
  const loadMoreStartDisplayedLenRef = useRef(0);
  const loadMoreStartRenderIndexRef = useRef(0);
  const pointerDownRef = useRef(false); // freeze appends while pointer down
  const lastScrollSaveAtRef = useRef(0);
  const scrollSaveRafRef = useRef(null);
  const pendingRestoreRef = useRef(null);
  const restoreTriesRef = useRef(0);
  const restoreRafRef = useRef(null);
  const restoreTimerRef = useRef(null);
  const restoreStartAtRef = useRef(0);
  const restoreResizeObsRef = useRef(null);
  const restoreInProgressRef = useRef(false);
  const buildAllStartedRef = useRef(false);
  const buildAllDoneRef = useRef(false);
  const fetchStartedRef = useRef(false);
  const fetchMoreRef = useRef(false);
  const savedStateRef = useRef(null); // { pos, idx, orderJson, orderLen }
  const scrollAppliedRef = useRef(false);
  const listRequestsRef = useRef([]);
  const restoreIntentRef = useRef(false);
  const restoreTokenRef = useRef(null);

  const homeScrollKey = useMemo(() => {
    if (isSearching) return null;
    const k = (keyword || '').toString().trim();
    return `home:${k || 'all'}`;
  }, [isSearching, keyword]);

  const storagePrefix = useMemo(() => {
    return homeScrollKey ? `homeScroll:${homeScrollKey}` : null;
  }, [homeScrollKey]);

  // One-shot "return from product" intent.
  // Uses sessionStorage `${storagePrefix}:navToken` and a global cache to survive React 18 StrictMode
  // double-mount in dev (prevents the classic "every other time" restore).
  useEffect(() => {
    restoreIntentRef.current = false;
    restoreTokenRef.current = null;
    if (!storagePrefix) return;

    try {
      const w = typeof window !== 'undefined' ? window : null;
      if (!w) return;
      const cacheKey = '__PROSHOP_HOME_RESTORE__';
      if (!w[cacheKey]) w[cacheKey] = {};

      let token = null;
      try {
        token = sessionStorage.getItem(`${storagePrefix}:navToken`);
      } catch (_) {
        token = null;
      }

      // Fallback: if we have a recently saved scroll state, assume we're returning
      // (covers cases where click capture didn't fire reliably).
      let recent = false;
      try {
        const tsStr = sessionStorage.getItem(`${storagePrefix}:ts`);
        const ts = tsStr ? parseInt(tsStr, 10) : NaN;
        if (Number.isFinite(ts) && Date.now() - ts < 10 * 60 * 1000) {
          const orderStr = sessionStorage.getItem(`${storagePrefix}:order`) || '';
          recent = !!orderStr;
        }
      } catch (_) {
        recent = false;
      }

      if (token) {
        // Persist token in cache then clear storage immediately.
        w[cacheKey][storagePrefix] = token;
        try {
          sessionStorage.removeItem(`${storagePrefix}:navToken`);
        } catch (_) {}
      } else if (w[cacheKey] && w[cacheKey][storagePrefix]) {
        token = w[cacheKey][storagePrefix];
      }

      if (token || recent) {
        restoreIntentRef.current = true;
        restoreTokenRef.current = token;
      }
    } catch (_) {}
  }, [storagePrefix]);

  const getSavedScrollState = useCallback(() => {
    if (!storagePrefix) return null;
    const posStr = sessionStorage.getItem(`${storagePrefix}:pos`);
    const winPosStr = sessionStorage.getItem(`${storagePrefix}:winPos`);
    const idxStr = sessionStorage.getItem(`${storagePrefix}:idx`);
    const orderStr = sessionStorage.getItem(`${storagePrefix}:order`);
    if (posStr && idxStr) {
      const pos = parseInt(posStr, 10);
      const winPos = winPosStr ? parseInt(winPosStr, 10) : 0;
      const idx = parseInt(idxStr, 10);
      if (Number.isFinite(pos) && Number.isFinite(idx)) {
        return {
          pos,
          winPos: Number.isFinite(winPos) ? winPos : 0,
          idx,
          order: orderStr || null,
          source: 'scoped',
        };
      }
    }
    // Backward compatibility (previous implementation)
    const legacyPos = sessionStorage.getItem('homeScreenScrollPos');
    const legacyWinPos = sessionStorage.getItem('homeScreenWindowScrollPos');
    const legacyIdx = sessionStorage.getItem('homeScreenRenderIndex');
    const legacyOrder = sessionStorage.getItem('homeScreenShuffledIds');
    if (legacyPos && legacyIdx) {
      const pos = parseInt(legacyPos, 10);
      const winPos = legacyWinPos ? parseInt(legacyWinPos, 10) : 0;
      const idx = parseInt(legacyIdx, 10);
      if (Number.isFinite(pos) && Number.isFinite(idx)) {
        return {
          pos,
          winPos: Number.isFinite(winPos) ? winPos : 0,
          idx,
          order: legacyOrder || null,
          source: 'legacy',
        };
      }
    }
    return null;
  }, [storagePrefix]);

  const maybeFinishRestore = useCallback(() => {
    // Finish (allow appends) as soon as scrollTop is applied.
    if (!scrollAppliedRef.current) return;
    restoreInProgressRef.current = false;
    restoreIntentRef.current = false;

    // Clear cached restore token (one-shot).
    try {
      const w = typeof window !== 'undefined' ? window : null;
      const cacheKey = '__PROSHOP_HOME_RESTORE__';
      if (w && w[cacheKey] && storagePrefix) {
        delete w[cacheKey][storagePrefix];
      }
    } catch (_) {}
  }, [storagePrefix]);

  const saveScrollStateNow = useCallback((opts = {}) => {
    if (isSearching || !storagePrefix) return;
    const el = containerRef.current;
    try {
      // Always mark intent even if the scroll container isn't available.
      if (opts && opts.markNavToProduct) {
        try {
          sessionStorage.setItem(`${storagePrefix}:navToken`, `${Date.now()}:${Math.random().toString(16).slice(2)}`);
        } catch (_) {}
      }

      if (!el) return;

      const pos = Math.max(0, Math.floor(el.scrollTop || 0));
      const winPos = Math.max(
        0,
        Math.floor(
          (typeof window !== 'undefined' && (window.pageYOffset || window.scrollY)) ||
            (typeof document !== 'undefined' && (document.documentElement?.scrollTop || document.body?.scrollTop)) ||
            0
        )
      );
      // Save the effective rendered count (renderIndex may exceed shuffledIds.length)
      const effectiveCount = Math.max(
        0,
        Math.floor(
          Math.min(
            typeof renderIndex === 'number' ? renderIndex : 0,
            Array.isArray(shuffledIds) ? shuffledIds.length : 0
          )
        )
      );

      // Persist the exact ordering so returning from Product doesn't reshuffle
      let orderJson = '';
      try {
        const order = Array.isArray(shuffledIds) ? shuffledIds.map((e) => ({ id: e?.id, cycle: e?.cycle })) : [];
        orderJson = JSON.stringify(order);
      } catch (_) {
        orderJson = '';
      }

      // Never clobber a previously saved, meaningful state with an "empty" snapshot
      // (this happens during route transitions when the container temporarily reports 0/empty).
      const currentOrderLen = Array.isArray(shuffledIds) ? shuffledIds.length : 0;
      const isMeaningful = effectiveCount > 0 && currentOrderLen > 0;

      // If we already have a non-zero saved scroll position, don't overwrite it with 0
      // (common during unmount/remount timing).
      let shouldWritePos = true;
      let existingPos = null;
      if (pos === 0) {
        try {
          const existingPosStr = sessionStorage.getItem(`${storagePrefix}:pos`);
          const parsed = existingPosStr ? parseInt(existingPosStr, 10) : NaN;
          if (Number.isFinite(parsed) && parsed > 0) {
            existingPos = parsed;
            shouldWritePos = false;
          }
        } catch (_) {}
      }

      // Same protection for the main window scroll.
      let shouldWriteWinPos = true;
      let existingWinPos = null;
      if (winPos === 0) {
        try {
          const existingWinPosStr = sessionStorage.getItem(`${storagePrefix}:winPos`);
          const parsed = existingWinPosStr ? parseInt(existingWinPosStr, 10) : NaN;
          if (Number.isFinite(parsed) && parsed > 0) {
            existingWinPos = parsed;
            shouldWriteWinPos = false;
          }
        } catch (_) {}
      }

      if (!isMeaningful) {
        try {
          const existingIdxStr = sessionStorage.getItem(`${storagePrefix}:idx`);
          const existingIdx = existingIdxStr ? parseInt(existingIdxStr, 10) : NaN;
          const existingOrder = sessionStorage.getItem(`${storagePrefix}:order`) || '';
          const hasExisting = (Number.isFinite(existingIdx) && existingIdx > 0) || existingOrder.length > 10;
          if (hasExisting) return;
        } catch (_) {}
      }

      if (shouldWritePos) {
        sessionStorage.setItem(`${storagePrefix}:pos`, String(pos));
      }
      if (shouldWriteWinPos) {
        sessionStorage.setItem(`${storagePrefix}:winPos`, String(winPos));
      }
      sessionStorage.setItem(`${storagePrefix}:idx`, String(effectiveCount));
      sessionStorage.setItem(`${storagePrefix}:ts`, String(Date.now()));
      if (orderJson) sessionStorage.setItem(`${storagePrefix}:order`, orderJson);

      // Keep a hot in-memory copy so restore never depends on storage timing.
      try {
        savedStateRef.current = {
          pos: shouldWritePos ? pos : (typeof existingPos === 'number' ? existingPos : pos),
          winPos: shouldWriteWinPos ? winPos : (typeof existingWinPos === 'number' ? existingWinPos : winPos),
          idx: effectiveCount,
          order: orderJson || null,
          orderLen: Array.isArray(shuffledIds) ? shuffledIds.length : 0,
        };
      } catch (_) {}

      // Also write legacy keys so any older code paths still restore
      if (shouldWritePos) {
        sessionStorage.setItem('homeScreenScrollPos', String(pos));
      }
      if (shouldWriteWinPos) {
        sessionStorage.setItem('homeScreenWindowScrollPos', String(winPos));
      }
      sessionStorage.setItem('homeScreenRenderIndex', String(effectiveCount));
      if (orderJson) sessionStorage.setItem('homeScreenShuffledIds', orderJson);
    } catch (_) {}
  }, [isSearching, storagePrefix, renderIndex, shuffledIds]);

  const scheduleThrottledScrollSave = useCallback(() => {
    if (isSearching || !storagePrefix) return;
    const now = Date.now();
    if (now - lastScrollSaveAtRef.current < 200) return;
    lastScrollSaveAtRef.current = now;
    try {
      if (scrollSaveRafRef.current) cancelAnimationFrame(scrollSaveRafRef.current);
    } catch (_) {}
    scrollSaveRafRef.current = requestAnimationFrame(() => {
      scrollSaveRafRef.current = null;
      saveScrollStateNow();
    });
  }, [isSearching, storagePrefix, saveScrollStateNow]);

  const onContainerClickCapture = useCallback(
    (e) => {
      if (isSearching) return;
      const target = e?.target;
      if (!target || typeof target.closest !== 'function') return;
      const anchor = target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      // Only capture navigation to product pages
      if (href.startsWith('/product/')) {
        saveScrollStateNow({ markNavToProduct: true });
      }
    },
    [isSearching, saveScrollStateNow]
  );

  const onContainerPointerDownCapture = useCallback(
    (e) => {
      if (isSearching) return;
      const target = e?.target;
      if (!target || typeof target.closest !== 'function') return;
      const anchor = target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      if (href.startsWith('/product/')) {
        // Save as early as possible to beat route changes and default click handling.
        saveScrollStateNow({ markNavToProduct: true });
      }
    },
    [isSearching, saveScrollStateNow]
  );

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset per-feed guards when route context changes
  useEffect(() => {
    buildAllStartedRef.current = false;
    buildAllDoneRef.current = false;
    fetchStartedRef.current = false;
    fetchMoreRef.current = false;
    pendingRestoreRef.current = null;
    restoreTriesRef.current = 0;
    scrollAppliedRef.current = false;
    restoreInProgressRef.current = false;
    initialOrderSetRef.current = false;
    renderIndexRef.current = 0;
    loadMoreStartedAtRef.current = 0;
    loadMoreStartDisplayedLenRef.current = 0;
    loadMoreStartRenderIndexRef.current = 0;
    setLoadingMoreUi(false);
    
    // Clear infinite scroll state to prevent cross-contamination
    if (isSearching) {
      setMasterList([]);
      setShuffledIds([]);
      setRenderIndex(0);
      cycleRef.current = 0;
    }
    // Do not wipe savedStateRef here; it is intentionally persistent across navigations.
  }, [storagePrefix, keyword, isSearching]);

  // Clear the "fetch more" guard when the list request finishes.
  useEffect(() => {
    if (!loading) {
      fetchMoreRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    renderIndexRef.current = typeof renderIndex === 'number' ? renderIndex : 0;
  }, [renderIndex]);

  const adaptiveBatch = useMemo(() => {
    // Fixed batch size: 40 items per fetch/reveal.
    // (Duplicates across fetches are allowed; each fetch response should be unique server-side.)
    return 40;
  }, []);

  // Fetch products
  useEffect(() => {
    if (isSearching) {
      // Reset fetch guard for new searches to ensure fetch always happens
      fetchStartedRef.current = false;

      // Abort any request still in flight from browsing the home feed
      // before it became a search -- otherwise that random batch keeps
      // running for no reason (productSlice's staleness guard already
      // makes it harmless if it lands late, but there's no point letting
      // an irrelevant DB round-trip finish).
      const staleReqs = Array.isArray(listRequestsRef.current) ? listRequestsRef.current : [];
      staleReqs.forEach((r) => {
        try {
          if (r && typeof r.abort === 'function') r.abort();
        } catch (_) {}
      });
      listRequestsRef.current = [];

      // For search, fetch requested page and do not enable infinite scroll
      // prefetch. Registered in listRequestsRef too, so it's aborted the
      // same way if the user searches again (or leaves) before this lands.
      const searchReq = dispatch(
        listProducts({ keyword, sortBy: 'name', order: 'asc', adminCheck: 'undefined', pageNumber, append: false })
      );
      listRequestsRef.current.push(searchReq);
      return;
    } else {
      // Guard against refetch loops: for the default home feed we only kick off the initial
      // fetch once per feed key. Subsequent state updates should not retrigger it.
      if (fetchStartedRef.current) return;
      const saved = getSavedScrollState();
      const restoreIntent = restoreIntentRef.current;
      // When returning from ProductScreen, do not refetch; rely on cached Redux list to preserve state.
      if (restoreIntent && saved && Array.isArray(products) && products.length > 0) {
        fetchStartedRef.current = true;
        return;
      }
      // For non-search, fetch the first batch.
      // Use random batches so Home feels varied; stable ordering is enforced client-side via shuffledIds.
      fetchStartedRef.current = true;
      const req = dispatch(
        listProducts({
          keyword: '',
          sortBy: 'rating',
          order: 'desc',
          adminCheck: 'undefined',
          pageNumber: 1,
          pageSize: 24,
          random: true,
          append: false,
        })
      );
      listRequestsRef.current.push(req);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes 'products': this effect dispatches the fetch that updates it, and the fetchStartedRef guard above exists specifically to prevent refetch loops
  }, [dispatch, keyword, pageNumber, isSearching, getSavedScrollState]);

  // Abort any in-flight list requests when leaving Home (prevents background updates while viewing a product)
  useEffect(() => {
    return () => {
      const reqs = Array.isArray(listRequestsRef.current) ? listRequestsRef.current : [];
      reqs.forEach((r) => {
        try {
          if (r && typeof r.abort === 'function') r.abort();
        } catch (_) {}
      });
      listRequestsRef.current = [];
    };
  }, []);

  // Note: We do NOT prefetch all pages up-front.
  // Infinite scroll fetches additional pages as the user reaches the end of the grid.

  // Keep masterList in sync with store once we finished loading pages (only when not searching)
  useEffect(() => {
    if (isSearching) {
      // Keep masterList empty during search to prevent contamination
      if (masterList.length > 0) {
        setMasterList([]);
      }
      return;
    }
    if (!loading && products?.length) {
      // Keep store order; HomeScreen display order is controlled via shuffledIds
      setMasterList([...products]);
    }
  }, [loading, products, isSearching, masterList.length]);

  // Initialize / maintain shuffledIds when master list changes (only when not searching)
  // Key rules:
  // - Never rebuild/shuffle the existing order due to prefetch appends.
  // - Append newly discovered IDs to the end in stable order.
  // - Keep renderIndex monotonic.
  useEffect(() => {
    if (isSearching) return;
    if (!masterList?.length) return;

    const ids = masterList.map((p) => p._id).filter(Boolean);
    if (ids.length === 0) return;

    // If we already have an order, append new entries (non-destructive).
    // Duplicates across fetches are allowed, so we do NOT de-dupe by id.
    if (Array.isArray(shuffledIds) && shuffledIds.length > 0) {
      if (masterList.length > shuffledIds.length) {
        const start = shuffledIds.length;
        const next = masterList
          .slice(start)
          .map((p, idx) => ({ id: p?._id, cycle: start + idx }))
          .filter((e) => Boolean(e.id));
        if (next.length > 0) {
          setShuffledIds((prev) => {
            const base = Array.isArray(prev) ? prev : [];
            return [...base, ...next];
          });
        }
      }
      return;
    }

    // If we already kicked off initial ordering but state hasn't landed yet, do nothing.
    if (initialOrderSetRef.current) return;

    // First-time initialization for this feed.
    const saved = savedStateRef.current || getSavedScrollState();
    const restoreIntent = restoreIntentRef.current;

    let restoredOrder = null;
    if (restoreIntent && saved?.order) {
      try {
        const parsed = JSON.parse(saved.order);
        if (Array.isArray(parsed) && parsed.length > 0) {
          if (!savedStateRef.current) {
            savedStateRef.current = {
              pos: typeof saved.pos === 'number' ? saved.pos : 0,
              idx: typeof saved.idx === 'number' ? saved.idx : 0,
              order: saved.order,
              orderLen: parsed.length,
            };
          }
          const allowed = new Set(ids);
          const cleaned = parsed
            .map((e) => ({ id: e?.id, cycle: Number(e?.cycle) || 0 }))
            .filter((e) => e?.id && allowed.has(e.id));
          if (cleaned.length > 0) restoredOrder = cleaned;
        }
      } catch (_) {
        restoredOrder = null;
      }
    }

    if (restoredOrder) {
      initialOrderSetRef.current = true;
      restoreInProgressRef.current = true;
      setShuffledIds(restoredOrder);
      const maxCycle = restoredOrder.reduce((m, e) => (e.cycle > m ? e.cycle : m), 0);
      cycleRef.current = maxCycle;
      const desired = typeof savedStateRef.current?.idx === 'number' ? savedStateRef.current.idx : (typeof saved?.idx === 'number' ? saved.idx : 0);
      const clamped = Math.min(Math.max(desired, adaptiveBatch), restoredOrder.length);
      setRenderIndex((prev) => (prev < clamped ? clamped : prev));
    } else {
      // Initial load: randomize once (do not reshuffle on subsequent store updates)
      initialOrderSetRef.current = true;
      // `cycle` is used as a unique key so duplicates can render later.
      setShuffledIds(shuffle(ids).map((id, idx) => ({ id, cycle: idx })));
      setRenderIndex((prev) => (prev < Math.min(adaptiveBatch, ids.length) ? Math.min(adaptiveBatch, ids.length) : prev));
    }

    const savedPos = (savedStateRef.current && typeof savedStateRef.current.pos === 'number') ? savedStateRef.current.pos : saved?.pos;
    if ((savedPos === null || savedPos === undefined) && containerRef.current) containerRef.current.scrollTop = 0;
  }, [masterList, isSearching, adaptiveBatch, getSavedScrollState, shuffledIds]);

  // Save scroll position on unmount for restoration when returning
  useEffect(() => {
    const container = containerRef.current;
    return () => {
      if (!isSearching && container) {
        saveScrollStateNow();
      }
    };
  }, [isSearching, saveScrollStateNow]);

  // Compute a map for quick lookup by id
  const masterMap = useMemo(() => {
    const m = new Map();
    for (const p of masterList) if (p?._id) m.set(p._id, p);
    return m;
  }, [masterList]);

  // Compute the products to display based on shuffled order and renderIndex
  const displayed = useMemo(() => {
    // During search, infinite scroll is disabled - return empty to prevent contamination
    if (isSearching) return [];
    if (!shuffledIds.length || !masterList.length) return [];
    const slice = shuffledIds.slice(0, renderIndex);
    return slice
      .map((e) => ({ entry: e, product: masterMap.get(e.id) }))
      .filter((x) => Boolean(x.product));
  }, [isSearching, shuffledIds, renderIndex, masterList, masterMap]);

  // Hide the "loading more" UI spinner once new items actually render.
  // Use a normal effect (not layout effect) so the spinner can paint at least once
  // on real devices before being removed.
  useEffect(() => {
    if (!loadingMoreUi) return;
    const startedAt = loadMoreStartedAtRef.current || 0;
    const startLen = loadMoreStartDisplayedLenRef.current || 0;
    const startIdx = loadMoreStartRenderIndexRef.current || 0;

    // If renderIndex advanced within the current shuffledIds, new cards should have rendered.
    // (When renderIndex runs past shuffledIds.length, we may be waiting on a cycle-append.)
    const renderIndexAdvanced = renderIndex > startIdx && renderIndex <= shuffledIds.length;

    // If new products are visible, stop the spinner.
    if (displayed.length > startLen || renderIndexAdvanced) {
      setLoadingMoreUi(false);
      return;
    }

    // Safety: if nothing changes for a while (should be rare), stop the spinner.
    if (startedAt && Date.now() - startedAt > 4000) {
      setLoadingMoreUi(false);
    }
  }, [loadingMoreUi, displayed.length, renderIndex, shuffledIds.length]);

  // Phase 1: read saved scroll state and request enough items
  useEffect(() => {
    if (isSearching) return;
    if (loading) return;
    if (!containerRef.current) return;
    if (pendingRestoreRef.current) return;
    if (!restoreIntentRef.current) return;
    const saved = savedStateRef.current || getSavedScrollState();
    if (!saved) return;
    restoreInProgressRef.current = true;
    const pos = typeof savedStateRef.current?.pos === 'number' ? savedStateRef.current.pos : saved.pos;
    const winPos = typeof savedStateRef.current?.winPos === 'number' ? savedStateRef.current.winPos : (typeof saved.winPos === 'number' ? saved.winPos : 0);
    const idx = typeof savedStateRef.current?.idx === 'number' ? savedStateRef.current.idx : saved.idx;
    pendingRestoreRef.current = { pos, winPos, idx };
    if (idx > renderIndex) setRenderIndex(idx);
  }, [isSearching, loading, getSavedScrollState, renderIndex]);

  // Phase 2: once enough items are rendered, apply scrollTop with a few retries
  useEffect(() => {
    if (isSearching) return;
    if (loading) return;
    const pending = pendingRestoreRef.current;
    const el = containerRef.current;
    if (!pending || !el) return;
    if (renderIndex < pending.idx) return;
    if (displayed.length === 0) return;

    const tryApply = () => {
      const target = Math.max(0, pending.pos);
      const winTarget = Math.max(0, pending.winPos || 0);

      // Restore main window scroll as well (some layouts scroll the page, not just the container).
      try {
        if (typeof window !== 'undefined') {
          const doc = document && document.documentElement ? document.documentElement : null;
          const body = document && document.body ? document.body : null;
          const winMax = Math.max(0, (doc?.scrollHeight || body?.scrollHeight || 0) - (window.innerHeight || 0));
          const clampedWin = Math.min(winTarget, winMax);
          if (Math.abs((window.scrollY || window.pageYOffset || 0) - clampedWin) > 2) {
            window.scrollTo(0, clampedWin);
          }
        }
      } catch (_) {}

      // Only attempt to apply when we can actually scroll to the target.
      const maxScrollable = Math.max(0, (el.scrollHeight || 0) - (el.clientHeight || 0));
      const heightReady = target <= maxScrollable + 2;
      if (heightReady) {
        try {
          el.scrollTop = target;
        } catch (_) {}
      }

      const ok = heightReady && Math.abs((el.scrollTop || 0) - target) <= 2;
      if (ok) {
        pendingRestoreRef.current = null;
        restoreTriesRef.current = 0;
        try { if (restoreRafRef.current) cancelAnimationFrame(restoreRafRef.current); } catch (_) {}
        restoreRafRef.current = null;
        try { if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current); } catch (_) {}
        restoreTimerRef.current = null;
        try { if (restoreResizeObsRef.current) restoreResizeObsRef.current.disconnect(); } catch (_) {}
        restoreResizeObsRef.current = null;
        scrollAppliedRef.current = true;
        try { maybeFinishRestore(); } catch (_) {}
        return;
      }

      const startedAt = restoreStartAtRef.current || Date.now();
      restoreStartAtRef.current = startedAt;
      restoreTriesRef.current += 1;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= 10000) {
        pendingRestoreRef.current = null;
        restoreTriesRef.current = 0;
        try { if (restoreRafRef.current) cancelAnimationFrame(restoreRafRef.current); } catch (_) {}
        restoreRafRef.current = null;
        try { if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current); } catch (_) {}
        restoreTimerRef.current = null;
        try { if (restoreResizeObsRef.current) restoreResizeObsRef.current.disconnect(); } catch (_) {}
        restoreResizeObsRef.current = null;
        scrollAppliedRef.current = false;
        // Never allow a failed restore attempt to permanently disable infinite scroll.
        restoreInProgressRef.current = false;
        restoreIntentRef.current = false;
        // Keep saved state if we couldn't apply (avoids losing it on flaky layouts)
        return;
      }

      // Keep trying as layout settles / images load.
      try { if (restoreRafRef.current) cancelAnimationFrame(restoreRafRef.current); } catch (_) {}
      restoreRafRef.current = requestAnimationFrame(tryApply);
      try { if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current); } catch (_) {}
      restoreTimerRef.current = setTimeout(tryApply, 60);
    };

    try { if (restoreRafRef.current) cancelAnimationFrame(restoreRafRef.current); } catch (_) {}
    try { if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current); } catch (_) {}
    restoreTimerRef.current = null;
    restoreStartAtRef.current = Date.now();

    // Retry immediately on size changes (images/layout).
    try {
      if (typeof ResizeObserver !== 'undefined') {
        restoreResizeObsRef.current = new ResizeObserver(() => {
          tryApply();
        });
        restoreResizeObsRef.current.observe(el);
      }
    } catch (_) {}

    restoreRafRef.current = requestAnimationFrame(tryApply);

    return () => {
      try { if (restoreRafRef.current) cancelAnimationFrame(restoreRafRef.current); } catch (_) {}
      restoreRafRef.current = null;
      try { if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current); } catch (_) {}
      restoreTimerRef.current = null;
      try { if (restoreResizeObsRef.current) restoreResizeObsRef.current.disconnect(); } catch (_) {}
      restoreResizeObsRef.current = null;
    };
  }, [isSearching, loading, renderIndex, displayed.length, maybeFinishRestore]);

  const triggerLoadMore = useCallback(() => {
    if (isSearching) return;
    if (restoreInProgressRef.current) return;
    if (loadingMoreRef.current || pointerDownRef.current) return;

    loadingMoreRef.current = true;

    // If we're at the end of what's currently loaded, fetch more products from the DB
    // and append them. We do NOT send exclude so we never exhaust the pool;
    // duplicates across fetches are allowed.
    try {
      const loadedCount = Array.isArray(masterList) ? masterList.length : 0;
      const nearEndOfLoaded = loadedCount > 0 && (renderIndexRef.current || 0) >= loadedCount - 2;
      if (!loading && !fetchMoreRef.current && nearEndOfLoaded) {
        fetchMoreRef.current = true;
        const req = dispatch(
          listProducts({
            keyword: '',
            sortBy: 'rating',
            order: 'desc',
            adminCheck: 'undefined',
            pageNumber: 1,
            pageSize: adaptiveBatch,
            random: true,
            append: true,
          })
        );
        listRequestsRef.current.push(req);
      }
    } catch (_) {}

    // Show spinner immediately while we reveal/append the next batch.
    if (!loadingMoreUi) {
      loadMoreStartedAtRef.current = Date.now();
      loadMoreStartDisplayedLenRef.current = displayed.length;
      loadMoreStartRenderIndexRef.current = typeof renderIndexRef.current === 'number' ? renderIndexRef.current : 0;
      setLoadingMoreUi(true);
    }

    // Reveal more on the next tick so the spinner can show before new cards render.
    setTimeout(() => {
      setRenderIndex((prev) => prev + adaptiveBatch);
      loadingMoreRef.current = false;
    }, 0);
  }, [
    isSearching,
    loading,
    loadingMoreUi,
    displayed.length,
    adaptiveBatch,
    dispatch,
    masterList,
  ]);

  // Load more items when the user reaches the end of the products grid.
  // IMPORTANT: On the real Home page, the document/window often scrolls (carousel/header/footer)
  // even if the products container exists. Tests tend to scroll the container directly.
  // Support BOTH:
  // - container scroll when it is actually scrollable
  // - window scroll when the container is not the active scroller
  const onScroll = useCallback(() => {
    if (isSearching) return;
    const el = containerRef.current;
    const gridEl = gridRef.current;
    if (!gridEl) return;

    // If the container isn't scrollable (content shorter than its viewport), the page will scroll.
    const containerScrollable =
      !!el &&
      Number.isFinite(el.scrollHeight) &&
      Number.isFinite(el.clientHeight) &&
      el.scrollHeight - el.clientHeight > 2;

    // Continuously persist scroll state (throttled) so navigation always restores correctly
    // (relies on containerRef existing; window scroll position is saved too)
    scheduleThrottledScrollSave();

    if (restoreInProgressRef.current) return;
    if (loadingMoreRef.current || pointerDownRef.current) return;
    // Only trigger load-more when the user has reached the end of the products grid.
    // - container mode: compare scrollTop+clientHeight to grid offset bottom
    // - window mode: compare grid's viewport bottom to window innerHeight
    let atGridEnd = false;
    if (containerScrollable && el) {
      // Use bounding rect math instead of offsetTop to avoid offsetParent/layout edge cases.
      try {
        const containerRect = el.getBoundingClientRect();
        const gridRect = gridEl.getBoundingClientRect();
        atGridEnd = gridRect.bottom <= containerRect.bottom + 2;
      } catch (_) {
        atGridEnd = false;
      }
    } else {
      try {
        const rect = gridEl.getBoundingClientRect();
        const viewportBottom = (typeof window !== 'undefined' && window.innerHeight) || 0;
        atGridEnd = rect.bottom <= viewportBottom + 2;
      } catch (_) {
        atGridEnd = false;
      }
    }

    if (atGridEnd) {
      triggerLoadMore();
    }
  }, [isSearching, scheduleThrottledScrollSave, triggerLoadMore]);

  // IntersectionObserver-based end-of-grid detection.
  // This is more reliable than scroll math in real browsers (especially with momentum scrolling).
  useEffect(() => {
    if (isSearching) return;
    const sentinel = gridEndSentinelRef.current;
    if (!sentinel) return;

    const el = containerRef.current;
    const containerScrollable =
      !!el &&
      Number.isFinite(el.scrollHeight) &&
      Number.isFinite(el.clientHeight) &&
      el.scrollHeight - el.clientHeight > 2;

    const root = containerScrollable ? el : null;

    let obs;
    try {
      obs = new IntersectionObserver(
        (entries) => {
          const hit = Array.isArray(entries) && entries.some((e) => e && e.isIntersecting);
          if (!hit) return;
          scheduleThrottledScrollSave();
          triggerLoadMore();
        },
        { root, threshold: 0.01 }
      );
      obs.observe(sentinel);
    } catch (_) {
      // If IntersectionObserver isn't available, scroll handler remains as fallback.
      obs = null;
    }

    return () => {
      try {
        if (obs) obs.disconnect();
      } catch (_) {}
    };
  }, [isSearching, scheduleThrottledScrollSave, triggerLoadMore, displayed.length]);

  useEffect(() => {
    if (isSearching) return;
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [onScroll, isSearching]);

  // Also listen to window scroll (common on Home due to carousel/header/footer).
  useEffect(() => {
    if (isSearching) return;
    if (typeof window === 'undefined') return;
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll, isSearching]);

  // Prevent page/body scroll when wheel action occurs over the grid at bounds
  // Attach a non-passive wheel listener to allow preventDefault without console warnings
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      
      // Only prevent scroll at bottom when scrolling down
      // Allow scroll to transfer to parent when at top and scrolling up
      if (atBottom && e.deltaY > 0) {
        e.preventDefault();
        e.stopPropagation();
      }
      // When at top and scrolling up, allow event to propagate to parent window
      // (no preventDefault, no stopPropagation)
    };
    el.addEventListener('wheel', wheelHandler, { passive: false });
    return () => {
      el.removeEventListener('wheel', wheelHandler, { passive: false });
    };
  }, []);

  // Optional: contain keyboard-based scrolling within grid when focused
  const onKeyDown = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const keys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', 'Space'];
    if (!keys.includes(e.key)) return;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    const goingDown = ['ArrowDown', 'PageDown', 'Space', 'End'].includes(e.key);
    const goingUp = ['ArrowUp', 'PageUp', 'Home'].includes(e.key);
    if ((atTop && goingUp) || (atBottom && goingDown)) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Meta />
      {!keyword ? (
        <ProductCarousel />
      ) : (
        <Link to='/' className='btn btn-light'>
          Go Back
        </Link>
      )}
      <h1>Latest Products</h1>
      {error ? (
        <Message variant='danger'>{error}</Message>
      ) : isSearching ? (
        // Classic list + pagination when searching (infinite scroll disabled)
        <>
          {loading && <Loader />}
          <div className='products-grid'>
            {(products || []).map((product) => (
              <div key={product._id} className='py-2'>
                <Product
                  product={{
                    ...product,
                    name: product.name.length > 40 ? product.name.substring(0, 60) + '…' : product.name,
                  }}
                />
              </div>
            ))}
          </div>
          <Paginate pages={pages} page={page} keyword={keyword ? keyword : ''} />
        </>
      ) : (
        // Infinite scrolling list when not searching
        <>
          {loading && products.length === 0 ? (
            <Loader />
          ) : (
          <div
            ref={containerRef}
            style={{
              height: 'calc(100vh - 140px)',
              overflowY: 'auto',
              overscrollBehavior: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
            className="no-scrollbar"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onClickCapture={onContainerClickCapture}
            onPointerDownCapture={onContainerPointerDownCapture}
            onMouseDown={() => { pointerDownRef.current = true; }}
            onMouseUp={() => { pointerDownRef.current = false; }}
            onMouseLeave={() => { pointerDownRef.current = false; }}
            onTouchStart={() => { pointerDownRef.current = true; }}
            onTouchEnd={() => { pointerDownRef.current = false; }}
          >
            <style>{`
              .no-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>
            <div className='products-grid' ref={gridRef}>
              {displayed.map(({ entry, product }) => (
                <div key={`${entry.id}-${entry.cycle}`} className='py-2'>
                  <Product
                    product={{
                      ...product,
                      name: product.name.length > 40 ? product.name.substring(0, 60) + '…' : product.name,
                    }}
                  />
                </div>
              ))}
            </div>

            <div ref={gridEndSentinelRef} style={{ height: 1 }} />

            {loadingMoreUi && (
              <div
                style={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                <Loader />
              </div>
            )}
          </div>
          )}
        </>
      )}
    </>
  );
};

export default HomeScreen;



// import React, { useEffect } from 'react'
// import { Link } from 'react-router-dom'
// import { useDispatch, useSelector } from 'react-redux'
// import { Row, Col } from 'react-bootstrap'
// import Product from '../components/Product'
// import Message from '../components/Message'
// import Loader from '../components/Loader'
// import Paginate from '../components/Paginate'
// import ProductCarousel from '../components/ProductCarousel'
// import Meta from '../components/Meta'
// import { listProducts } from '../actions/productActions'
// // import InfiniteScroll from 'react-infinite-scroll-component'
// // import { shuffle } from 'lodash'




// const HomeScreen = ({ match }) => {
//   const keyword = match.params.keyword

//   const pageNumber = match.params.pageNumber || 1

//   const dispatch = useDispatch()

//   const productList = useSelector((state) => state.productList)
//   const { loading, error, products, page, pages } = productList


//   useEffect(() => {
//     dispatch(listProducts(keyword, pageNumber))
    
//   }, [dispatch, keyword, pageNumber])

//   return (
//     <>
//     <div><Meta />
//       {!keyword ? (
//         <ProductCarousel />
//       ) : (
//         <Link to='/' className='btn btn-light'>
//           Go Back
//         </Link>
//       )}
//       <h1>Latest Products</h1>
//       {loading ? (
//         <Loader />
//       ) : error ? (
//         <Message variant='danger'>{error}</Message>
//       ) : (
//         <>
        
//           <Row>
//             {products.map((product) => (
//               <Col key={product._id} xs={6} sm={6} md={6} lg={4} xl={3}>
//                 <Product product={{
//                   ...product,
//                   name: product.name.length > 25 ? product.name.substring(0, 40) + '.......' : product.name
//                 }} />
//               </Col>
//             ))}
//           </Row>
//           <Paginate
//             pages={pages}
//             page={page}
//             keyword={keyword ? keyword : ''}
//           />
//         </>
//       )}</div>
      
//     </>
//   )
// }

// export default HomeScreen

// import React, { useEffect, useRef } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { Row, Col } from 'react-bootstrap';
// import Product from '../components/Product';
// import Message from '../components/Message';
// import Loader from '../components/Loader';
// import ProductCarousel from '../components/ProductCarousel';
// import Meta from '../components/Meta';
// import { listProducts } from '../actions/productActions';

// const HomeScreen = () => {
//   const dispatch = useDispatch();
//   const productsRef = useRef([]);
//   const loadingMore = useRef(false); // Track whether more products are being loaded

//   const productList = useSelector((state) => state.productList);
//   const { loading, error, products} = productList;

//   const observer = useRef(
//     new IntersectionObserver(
//       (entries) => {
//         const target = entries[0];
//         if (target.isIntersecting && !loadingMore.current) {
//           // Fetch more products when the last product is in view and no ongoing loading
//           loadingMore.current = true;
//           dispatch(listProducts(''));
//         }
//       },
//       { threshold: 1 } // Trigger when the target is fully in view
//     )
//   );

//   useEffect(() => {
//     // Observe the last product in the list
//     const targetProduct = productsRef.current[products.length - 1];
//     if (targetProduct) {
//       observer.current.observe(targetProduct);
//     }

//     // Cleanup the observer when the component unmounts
//     return () => observer.current.disconnect();
//   }, [products]);

//   useEffect(() => {
//     // Reset the loadingMore flag when new products are received
//     loadingMore.current = false;
//   }, [products]);

//   useEffect(() => {
//     dispatch(listProducts(''));
//   }, [dispatch]);

//   return (
//     <>
//       <div style={{ marginLeft: '140px' }}>
//         <Meta />
//         <ProductCarousel />
//         <h1>Latest Products</h1>
//         <Row>
//           {products.map((product, index) => (
//             <Col key={product._id} sm={12} md={6} lg={4} xl={3}>
//               <Product product={product} />
//               {index === products.length - 1 && (
//                 <div ref={(ref) => (productsRef.current[index] = ref)} />
//               )}
//             </Col>
//           ))}
//         </Row>
//         {loading ? (
//           <Loader />
//         ) : error ? (
//           <Message variant="danger">{error}</Message>
//         ) : null}
//       </div>
//     </>
//   );
// };

// export default HomeScreen;